import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Card, Checkbox, Divider, Icon, Input, InputNumber, notification, Select} from 'antd';
import {getProductToReorderList, setSupplierToReorder} from './actions';
import {emailOrderToSupplier} from "../Supplier/actions";
import {getSuppliers} from "../Supplier/actions";
import {bindActionCreators} from "redux";
import ResponsiveTable from "../common/ResponsiveTable";
import moment from "moment";
import {Link} from "react-router-dom";

const NILUUID = '00000000-0000-0000-0000-000000000000';

function suggestedQuantity(quantitySold, overPeriodInDays, daysToReorder) {
  if (!overPeriodInDays) return 0;
  return (((quantitySold / overPeriodInDays) * daysToReorder) + 0.49).toFixed();
}

function toOrder(record, daysToReorder) {
  if (!record.overPeriodInDays) return 0;
  const value = suggestedQuantity(record.quantitySold, record.overPeriodInDays, daysToReorder) - (record.quantityInStock + record.quantityInOrder);
  if (value < 0) return '0';
  return value.toFixed();
}

const Statuses = {
  Initial: 0,
  Ok: 1,
  Error: 2,
};

// eslint-disable-next-line react/prop-types
const Status = ({value}) => {
  let type, color;
  switch (value) {
    case Statuses.Ok:
      type = "check-circle";
      color = "#52c41a";
      break;
    case Statuses.Error:
      type = "close-circle";
      color = "#ff0000";
      break;
    default:
      return '';
  }
  return <Icon type={type}
               theme="twoTone"
               twoToneColor={color} />;
};

const productToReorderListColumns = [
  {
    title: '', dataIndex: 'supplierId', key: 'supplierId',
  },
  {
    title: 'Product',
    dataIndex: 'productDisplay',
    key: 'productDisplay'
  },
  {
    title: 'Order Code',
    dataIndex: 'supplierOrderCode',
    key: 'orderCode'
  },
  {
    title: 'Quantity in Stock',
    dataIndex: 'quantityInStock',
    key: 'quantityInStock',
    render: (value, record) => value + (record.quantityInOrder ? ` + ${record.quantityInOrder}` : '')
  },
  {
    title: 'Quantity Delta',
    dataIndex: 'quantitySold',
    key: 'quantitySold'
  },
  {
    title: 'Over Period',
    dataIndex: 'overPeriodInDays',
    key: 'overPeriodInDays',
    render: value => value ? <span>{value} day(s)</span> : 'N/A'
  },
  {
    title: 'Suggested Quantity',
    dataIndex: 'suggestedQuantity',
    key: 'suggestedQuantity',
    render: (_, record) => <span>{record.suggestedQuantity}</span>
  },
  {
    title: 'Quantity to Order',
    dataIndex: 'toOrder',
    key: 'toOrder',
  },
  {
    title: 'View',
    key: 'view',
    fixed: 'right',
    width: 90,
    render: (_, item) => (
      <Link to={`/product/history/${encodeURIComponent(item.productId)}#NewWindow`} target="_blank">History</Link>
    ),
  },
];

class ReorderPage extends Component {
  static propTypes = {
    emailOrderToSupplier: PropTypes.func.isRequired,
  }

  state = {
    daysToReorder: 14,
    productsToReorder: [],
    quantitiesToReorder: {},
    selectedRows: [],
    statuses: {},
    errors: [],
    showAllProducts: false,
    notes: ''
  };

  constructor(props) {
    super(props);
    this._columns = productToReorderListColumns.map(x => ({...x}));
    this._columns[0].render = this._renderStatus;
    this._columns[7].render = this._renderInput;
    this.state.searchParams = new URLSearchParams(this.props.location.search);
  }

  componentDidMount() {
    const { searchParams } = this.state;
    const supplierId = searchParams.get('supplierId');
    const showAll = searchParams.get('showAll') === 'true';
    if (supplierId) this.props.setSupplierToReorder(supplierId);
    if (showAll) this.setState({ showAllProducts: true });
    if (!supplierId || !showAll) this._load().catch(console.error);
  }

  componentDidUpdate(prevProps) {
    if (this.props.salonId !== prevProps.salonId) this._load().catch(console.error);
    else if (this.props.supplierId !== prevProps.supplierId) this._load().catch(console.error);
  }

  _load = async() => {
    const {salonId, supplierId} = this.props;
    const {searchParams} = this.state;
    await this.props.getProductToReorderList({salonId, supplierId: supplierId || NILUUID, order: ['supplierName ASC', 'productDisplay ASC']});
    if (supplierId === NILUUID || searchParams.size) await this.props.getSuppliers({salonId});
    this._setTransformData(this.state.daysToReorder, this.state.showAllProducts);
  };

  _setTransformData = (daysToReorder, showAllProducts) => {
    const {searchParams} = this.state;
    let productsToReorder = this.props.productToReorderList
      .map((x) => {
        let overPeriodInDays = null;
        if (x.sinceDate) {
          overPeriodInDays = Math.ceil(moment().diff(x.sinceDate) / 86400000);
          if (overPeriodInDays < 7) overPeriodInDays = 7;
        }
        return {
          ...x,
          overPeriodInDays,
          suggestedQuantity: suggestedQuantity(x.quantitySold, overPeriodInDays, daysToReorder)
        };
      });
    const selectProductId = searchParams.get('selectProductId');
    if (selectProductId) {
      this.setState(prev => ({
        selectedRows: prev.selectedRows.length
          ? prev.selectedRows
          : productsToReorder.filter(x => x.productId === selectProductId)
      }));
    }
    if (!showAllProducts) {
      productsToReorder = productsToReorder.filter(p =>
        p.suggestedQuantity > p.quantityInStock || p.productId === selectProductId || p.quantityInStock <= 4
      );
    }
    productsToReorder = productsToReorder.map((x) => ({
      ...x,
      toOrder: toOrder(x, daysToReorder)
    }));
    const changes = {productsToReorder, daysToReorder};
    if (showAllProducts !== this.state.showAllProducts) {
      changes.showAllProducts = showAllProducts;
    }
    this.setState(changes);
  };

  _renderStatus = (value) => {
    const {statuses} = this.state;
    return <Status value={statuses[value]} />;
  }

  _renderInput = (value, record) => {
    const {quantitiesToReorder} = this.state;
    return (
      <InputNumber value={quantitiesToReorder[record.productId] || value}
                   onChange={qty => this._onToOrderChange(qty, record.productId)}
      />);
  }

  _onToOrderChange = (value, key) => {
    this.setState(state => ({quantitiesToReorder: {...state.quantitiesToReorder, [key]: value }}));
  }

  _emailSuppliers = async(e) => {
    e.preventDefault();
    const {selectedRows, quantitiesToReorder, notes} = this.state;
    const emailSuppliersMap = selectedRows.reduce((a, c) => {
      let sup = a[c.supplierId];
      if (!sup) {
        sup = {
          supplierId: c.supplierId,
          orderItems: [],
          notes
        };
      }
      const quantity = parseInt(quantitiesToReorder[c.productId] || c.toOrder, 10);
      if (quantity <= 0) return a;
      sup.orderItems.push({
        productId: c.productId,
        quantity,
      });
      a[c.supplierId] = sup;
      return a;
    }, {});
    const emailSuppliers = Object.keys(emailSuppliersMap).map(k => emailSuppliersMap[k]);
    const supplierNames = selectedRows.reduce((a,c) => ({...a, [c.supplierId]: c.supplierName}), {});
    for (const emailSupplier of emailSuppliers) {
      let status;
      try {
        console.log(emailSupplier);
        await this.props.emailOrderToSupplier(emailSupplier);
        status = Statuses.Ok;
        notification.success({message: `Order emailed to ${supplierNames[emailSupplier.supplierId]}. You should receive a copy in your inbox.`});
      } catch (err) {
        notification.error({message: `Failed to email supplier ${supplierNames[emailSupplier.supplierId]}: ${err.message}.`});
        status = Statuses.Error;
      }
      this.setState(prev => ({
        statuses: {...prev.statuses, [emailSupplier.supplierId]: status},
      }));
    }
  }

  render() {
    const {productsToReorder, daysToReorder, selectedRows, errors, showAllProducts} = this.state;
    const {suppliers, supplierId} = this.props;
    return (
      <Card title="Reorder">
        Supplier&nbsp;
        <Select
          style={{width: '200px', marginRight: '1em'}}
          value={supplierId}
          onChange={value => this.props.setSupplierToReorder(value)}
        >
          <Select.Option key="addProduct-supplierId-null" value={''}>NONE</Select.Option>
          {suppliers.map(supplier =>
            <Select.Option key={supplier.supplierId} value={supplier.supplierId}>{supplier.name}</Select.Option>
          )}
        </Select>
        &nbsp;Reorder for&nbsp;
        <InputNumber
          style={{width: '60px'}}
          value={daysToReorder}
          onChange={daysToReorder => this._setTransformData(daysToReorder, showAllProducts)}
        />
        &nbsp;days&nbsp;
        <Checkbox
          style={{marginLeft:'1em'}}
          checked={showAllProducts}
          onChange={() => this._setTransformData(daysToReorder, !showAllProducts)}
        />
        &nbsp;Show all products&nbsp;
        <Divider type="horizontal" />
        <ResponsiveTable
          rowSelection={{
            selectedRowKeys: selectedRows.map(x => x.productId),
            onChange: (keys, rows) => this.setState({selectedRows: rows}),
            getCheckboxProps: record => record.supplierId ? {} : {disabled: true},
          }}
          columns={this._columns}
          dataSource={productsToReorder}
          rowKey="productId"
          onChange={this._load}
          pagination={false}
        />
        <Input.TextArea
          style={{ margin: '1rem 0' }}
          placeholder="Enter notes for supplier"
          onChange={e => this.setState({ notes: e.target.value })}
          value={this.state.notes}
          disabled={!supplierId}
        />
        <Button type="primary"
                disabled={selectedRows.length === 0}
                onClick={this._emailSuppliers}
        >
          Email Supplier(s)
        </Button>
        <div className="error" style={{marginTop: 10}}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      </Card>
    );
  }
}

ReorderPage.propTypes = {
  salonId: PropTypes.string.isRequired,
  supplierId: PropTypes.string.isRequired,
  getSuppliers: PropTypes.func.isRequired,
  suppliers: PropTypes.array.isRequired,
  productToReorderList: PropTypes.array.isRequired,
  getProductToReorderList: PropTypes.func.isRequired,
  setSupplierToReorder: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
  supplierId: state.product.supplierToReorderId,
  suppliers: state.supplier.suppliers,
  productToReorderList: state.product.productToReorderList,
});

const mapDispatchToProps = dispatch => ({
  getProductToReorderList: bindActionCreators(getProductToReorderList, dispatch),
  emailOrderToSupplier: bindActionCreators(emailOrderToSupplier, dispatch),
  setSupplierToReorder: bindActionCreators(setSupplierToReorder, dispatch),
  getSuppliers: bindActionCreators(getSuppliers, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPage);



// WEBPACK FOOTER //
// ./src/Product/ReorderPage.js